/**
 * Aiuto contestuale di sezione: piccola icona "?" accanto al titolo che,
 * al passaggio del mouse o al focus/click, apre un riquadro con la
 * spiegazione della sezione (a cosa serve, cosa puoi farci).
 *
 * Il contenuto arriva dal registro centralizzato lib/help-content.ts,
 * ma si può passare anche `title`/`children` liberi.
 *
 * Uso rapido con registro:
 *   <HelpHint topic="feed" />
 * Uso con contenuto custom:
 *   <HelpHint title="Titolo" side="bottom">testo…</HelpHint>
 */
import { HelpCircle } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { HELP, type HelpTopic } from '../../lib/help-content';

type Side = 'bottom' | 'top' | 'left' | 'right';

const SIDE_POS: Record<Side, string> = {
  bottom: 'top-full left-0 mt-2',
  top: 'bottom-full left-0 mb-2',
  left: 'right-full top-0 mr-2',
  right: 'left-full top-0 ml-2',
};

export function HelpHint({
  topic,
  title,
  side = 'bottom',
  children,
}: {
  topic?: HelpTopic;
  title?: string;
  side?: Side;
  children?: ReactNode;
}) {
  // Il click "fissa" il riquadro (utile su touch); l'hover lo mostra comunque.
  const [pinned, setPinned] = useState(false);

  const entry = topic ? HELP[topic] : undefined;
  const heading = title ?? entry?.title ?? 'Aiuto';
  const body = children ?? entry?.body;

  return (
    <span className="group/help relative inline-flex align-middle">
      <button
        type="button"
        aria-label={`Aiuto: ${heading}`}
        onClick={() => setPinned((v) => !v)}
        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-zinc-500 transition-colors hover:text-accent-400 focus:text-accent-400 focus:outline-none"
      >
        <HelpCircle size={15} />
      </button>
      <div
        className={`absolute z-50 w-72 rounded-xl border p-3.5 text-left shadow-xl transition-opacity duration-100 group-hover/help:opacity-100 group-focus-within/help:opacity-100 ${SIDE_POS[side]} ${
          pinned ? 'opacity-100' : 'pointer-events-none opacity-0 group-hover/help:pointer-events-auto'
        }`}
        style={{ background: '#18181b', color: '#e4e4e7', border: '1px solid #3f3f46' }}
      >
        <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-zinc-100">
          <HelpCircle size={13} className="text-accent-400" />
          {heading}
        </div>
        <div className="space-y-1.5 text-[11px] leading-relaxed text-zinc-400">{body}</div>
      </div>
    </span>
  );
}
